import { Camera as CameraIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BottomSheet,
  Button,
  DropdownSelect,
  NumPad,
  SegmentedControl,
  type DropdownOption,
} from './';
import { useToast } from './Toast';
import { useActions, useAppData } from '../data';
import type { TransactionType } from '../data/types';
import { formatCurrency } from '../lib/currency';
import { toIsoDate } from '../lib/date';
import { FREE_RECEIPT_LIMIT } from '../lib/limits';
import { suggestCategoryFromMerchant } from '../lib/merchantCategories';
import {
  captureReceiptImage,
  extractReceiptText,
  parseReceipt,
  type ParsedReceipt,
} from '../lib/ocr';
import { supportsOCR } from '../lib/platform';
import {
  countReceipts,
  pruneOldestReceipts,
  saveReceipt,
} from '../lib/receiptStore';
import { useProContext } from '../pro';

type ScannerState = 'idle' | 'capturing' | 'processing' | 'review';

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved?: (txId: string) => void;
};

const TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: 'expense', label: 'Expense' },
  { value: 'income', label: 'Income' },
];

/**
 * Capture-and-OCR-a-receipt flow. Hooks the device camera, runs ML Kit
 * text recognition, parses merchant/amount/date, and lets the user
 * review before committing the transaction. Safe to mount on web —
 * the parent should still hide its launcher button via supportsOCR()
 * since the camera + OCR plugins only function on native.
 */
export function ReceiptScannerSheet({ open, onClose, onSaved }: Props) {
  const data = useAppData();
  const actions = useActions();
  const pro = useProContext();
  const toast = useToast();

  const ocrAvailable = supportsOCR();
  const [state, setState] = useState<ScannerState>('idle');
  const [imageB64, setImageB64] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedReceipt | null>(null);
  /** True when the review form is being filled by hand (no OCR ran or
   *  OCR failed). Surfaces an informational banner above the form. */
  const [manualOnly, setManualOnly] = useState(false);

  // Editable review form.
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState(0);
  const [amountOpen, setAmountOpen] = useState(false);
  const [date, setDate] = useState(toIsoDate(new Date()));
  const [type, setType] = useState<TransactionType>('expense');
  const [categoryId, setCategoryId] = useState('');
  const [warnLimit, setWarnLimit] = useState(false);

  // Reset everything whenever the sheet closes.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setState('idle');
      setImageB64(null);
      setParsed(null);
      setManualOnly(false);
      setDescription('');
      setAmount(0);
      setDate(toIsoDate(new Date()));
      setType('expense');
      setCategoryId('');
      setAmountOpen(false);
      setWarnLimit(false);
    }
    wasOpen.current = open;
  }, [open]);

  const categoryOptions: DropdownOption[] = useMemo(
    () =>
      data.categories
        .filter((c) => c.type === type)
        .sort((a, b) => a.order - b.order)
        .map((c) => ({ value: c.id, label: c.name })),
    [data.categories, type],
  );

  // Pin the dropdown to a valid option when type flips or list loads.
  useEffect(() => {
    if (
      categoryOptions.length > 0 &&
      !categoryOptions.find((o) => o.value === categoryId)
    ) {
      setCategoryId(categoryOptions[0]!.value);
    }
  }, [categoryOptions, categoryId]);

  const beginCapture = async () => {
    setState('capturing');
    const img = await captureReceiptImage();
    if (!img) {
      // User cancelled or permission denied — drop back to idle silently.
      setState('idle');
      return;
    }
    setImageB64(img);

    // No OCR available (web) → skip parse, go straight to manual review.
    if (!ocrAvailable) {
      setManualOnly(true);
      setParsed(null);
      setState('review');
      return;
    }

    setState('processing');
    try {
      const text = await extractReceiptText(img);
      const p = parseReceipt(text);
      setParsed(p);
      setDescription(p.merchant ?? '');
      if (p.amount !== null) setAmount(p.amount);
      if (p.date) setDate(p.date);
      const suggested = p.merchant
        ? suggestCategoryFromMerchant(p.merchant, data.categories) ?? ''
        : '';
      if (suggested) setCategoryId(suggested);
      setManualOnly(false);
      setState('review');
    } catch {
      // OCR failed (plugin missing, image unreadable, etc.) — keep the
      // photo and let the user fill the form by hand.
      setManualOnly(true);
      setParsed(null);
      setState('review');
    }
  };

  const handleSave = async () => {
    const trimmed = description.trim();
    if (!trimmed || amount <= 0 || !categoryId) return;

    // Free-tier receipt cap. Show a one-step warning the first time we
    // hit it, auto-prune on Continue.
    if (!pro.isPro) {
      const count = await countReceipts();
      if (count >= FREE_RECEIPT_LIMIT && !warnLimit) {
        setWarnLimit(true);
        return;
      }
      if (count >= FREE_RECEIPT_LIMIT) {
        const dropped = await pruneOldestReceipts(1);
        for (const { txId } of dropped) {
          actions.updateTransaction(txId, { receiptId: undefined });
        }
      }
    }

    const tx = actions.addTransaction({
      amount,
      type,
      categoryId,
      description: trimmed,
      date,
      source: 'manual',
    });

    if (imageB64) {
      const id = await saveReceipt(tx.id, imageB64);
      if (id) actions.updateTransaction(tx.id, { receiptId: id });
    }

    toast.show({ message: 'Receipt saved', kind: 'success' });
    onSaved?.(tx.id);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={ocrAvailable ? 'Scan receipt' : 'Attach receipt'}
    >
      <div className="px-screen-x py-4 flex flex-col gap-4">
        {state === 'idle' ? (
          <IdleView
            ocrAvailable={ocrAvailable}
            onCapture={beginCapture}
            onCancel={onClose}
          />
        ) : null}
        {state === 'capturing' || state === 'processing' ? (
          <ProcessingView label={state === 'capturing' ? 'Opening camera…' : 'Reading receipt…'} />
        ) : null}
        {state === 'review' ? (
          <ReviewView
            imageB64={imageB64}
            parsed={parsed}
            manualOnly={manualOnly}
            description={description}
            setDescription={setDescription}
            amount={amount}
            onAmountTap={() => setAmountOpen(true)}
            currency={data.profile.currency}
            date={date}
            setDate={setDate}
            type={type}
            setType={setType}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            categoryOptions={categoryOptions}
            warnLimit={warnLimit}
            onSave={handleSave}
          />
        ) : null}
      </div>

      <BottomSheet
        open={amountOpen}
        onClose={() => setAmountOpen(false)}
        title="Amount"
      >
        <NumPad
          initialValue={amount}
          currency={data.profile.currency}
          onDone={(v) => {
            setAmount(v);
            setAmountOpen(false);
          }}
        />
      </BottomSheet>
    </BottomSheet>
  );
}

// ---------- Sub-views ----------

function IdleView({
  ocrAvailable,
  onCapture,
  onCancel,
}: {
  ocrAvailable: boolean;
  onCapture: () => void;
  onCancel: () => void;
}) {
  const headline = ocrAvailable
    ? "Snap a receipt. We'll extract the merchant and amount."
    : 'Attach a receipt photo. Enter details below.';
  const cta = ocrAvailable ? 'Scan receipt' : 'Attach photo';
  return (
    <>
      <div className="flex flex-col items-center text-center gap-2 py-2">
        <div className="w-14 h-14 rounded-full bg-accent/10 inline-flex items-center justify-center text-accent">
          <CameraIcon size={26} strokeWidth={1.5} />
        </div>
        <p className="text-[14px] text-text-secondary max-w-[280px]">
          {headline}
        </p>
      </div>
      <Button variant="primary" size="lg" fullWidth onClick={onCapture}>
        {cta}
      </Button>
      <button
        type="button"
        onClick={onCancel}
        className="text-[13px] font-medium text-accent py-3 mx-auto"
      >
        Cancel
      </button>
    </>
  );
}

function ProcessingView({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-8">
      <Loader2 size={28} strokeWidth={1.75} className="animate-spin text-accent" />
      <p className="text-[14px] text-text-secondary">{label}</p>
    </div>
  );
}

function ReviewView({
  imageB64,
  parsed,
  manualOnly,
  description,
  setDescription,
  amount,
  onAmountTap,
  currency,
  date,
  setDate,
  type,
  setType,
  categoryId,
  setCategoryId,
  categoryOptions,
  warnLimit,
  onSave,
}: {
  imageB64: string | null;
  parsed: ParsedReceipt | null;
  manualOnly: boolean;
  description: string;
  setDescription: (v: string) => void;
  amount: number;
  onAmountTap: () => void;
  currency: string;
  date: string;
  setDate: (v: string) => void;
  type: TransactionType;
  setType: (v: TransactionType) => void;
  categoryId: string;
  setCategoryId: (v: string) => void;
  categoryOptions: DropdownOption[];
  warnLimit: boolean;
  onSave: () => void;
}) {
  return (
    <>
      {imageB64 ? (
        <img
          src={imageB64}
          alt="Receipt"
          className="self-center rounded-card max-h-[200px] object-contain"
        />
      ) : null}

      {manualOnly ? (
        <p className="text-[12px] text-text-secondary bg-accent/5 rounded-button px-3 py-2 -mt-1">
          Photo attached. Enter details manually.
        </p>
      ) : parsed && (parsed.merchant === null || parsed.amount === null) ? (
        <p className="text-[12px] text-text-tertiary text-center -mt-1">
          Couldn't read everything. Check the fields below.
        </p>
      ) : null}

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
          Description
        </span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] text-text-primary outline-none focus:ring-2 focus:ring-accent"
        />
      </label>

      <button
        type="button"
        onClick={onAmountTap}
        className="flex flex-col gap-1.5 text-left"
      >
        <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
          Amount
        </span>
        <span className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] font-mono tabular-nums text-text-primary">
          {formatCurrency(amount, { currency })}
        </span>
      </button>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
          Date
        </span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] text-text-primary outline-none focus:ring-2 focus:ring-accent"
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
          Type
        </span>
        <SegmentedControl options={TYPE_OPTIONS} value={type} onChange={setType} />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
          Category
        </span>
        <DropdownSelect
          options={categoryOptions}
          value={categoryId}
          onChange={setCategoryId}
        />
      </div>

      {warnLimit ? (
        <p className="text-[12px] text-warning bg-warning/10 rounded-button px-3 py-2">
          Free tier holds {FREE_RECEIPT_LIMIT} receipts. Saving this will
          remove the oldest. Tap Save again to continue.
        </p>
      ) : null}

      <Button
        variant="primary"
        size="lg"
        fullWidth
        disabled={!description.trim() || amount <= 0 || !categoryId}
        onClick={onSave}
      >
        Save transaction
      </Button>
    </>
  );
}
